import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import MasterDashboardHome from './MasterDashboardHome';
import PackageManagement from './PackageManagement';
import CreatePackage from './CreatePackage';
import ManagePackages from './ManagePackages';
import WarehouseManagement from './WarehouseManagement';
import InvoiceManagement from './InvoiceManagement';
import RatesAndServices from './RatesAndServices';
import PaymentManagement from './PaymentManagement';
import LockerManagement from './LockerManagement';
import ListLockersTable from './ListLockersTable';
import LockerAlerts from './LockerAlerts';
import LockerRepacks from './LockerRepacks';
import ClientImport from './ClientImport';
import CreateManualWR from './CreateManualWR';
import CreateManualInvoice from './CreateManualInvoice';
import CreateLocker from './CreateLocker';
import AccountStatement from './AccountStatement';
import AdminAlerts from './admin/AdminAlerts';
import AdminClients from './admin/AdminClients';
import AdminAnalytics from './admin/AdminAnalytics';
import AdminEmailLogs from './admin/AdminEmailLogs';
import AdminTrackPackage from './admin/AdminTrackPackage';
import WarehouseDetail from './admin/WarehouseDetail';
import InvoiceDetail from './admin/InvoiceDetail';
import { Menu, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MasterDashboard = () => {
  const { user, logout, token } = useAuth();
  const [currentSection, setCurrentSection] = useState('home');
  const [navigationParams, setNavigationParams] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sectionHistory, setSectionHistory] = useState([]);

  // Shared data states (fetched once, passed to child components)
  const [clients, setClients] = useState([]);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const navigateToSection = (section, params = null) => {
    setSectionHistory(prev => [...prev, currentSection]);
    setNavigationParams(params);
    setCurrentSection(section);
  };

  const goBack = () => {
    if (sectionHistory.length > 0) {
      const prev = sectionHistory[sectionHistory.length - 1];
      setSectionHistory(h => h.slice(0, -1));
      setNavigationParams(null);
      setCurrentSection(prev);
    } else {
      setNavigationParams(null);
      setCurrentSection('home');
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients`, axiosConfig);
      setClients(res.data);
    } catch (error) {
    }
  };

  const renderSection = () => {
    switch (currentSection) {
      case 'home':
        return <MasterDashboardHome onNavigate={setCurrentSection} />;
      case 'alerts':
        return <AdminAlerts token={token} clients={clients} />;
      case 'packages':
        return <PackageManagement token={token} clients={clients} onNavigate={navigateToSection} />;
      case 'create-package':
        return <CreatePackage token={token} clients={clients} />;
      case 'manage-packages':
        return <ManagePackages token={token} clients={clients} />;
      case 'track-package':
        return <AdminTrackPackage token={token} clients={clients} onNavigate={navigateToSection} />;
      case 'warehouse':
        return <WarehouseManagement 
          token={token} 
          clients={clients} 
          onNavigateToInvoice={(wrData) => navigateToSection('create-manual-invoice', { wrData })}
          onNavigate={navigateToSection}
          navigationParams={navigationParams}
        />;
      case 'invoices':
        return <InvoiceManagement token={token} clients={clients} onNavigate={navigateToSection} navigationParams={navigationParams} />;
      case 'payments':
        return <PaymentManagement token={token} clients={clients} />;
      case 'account-statement':
        return <AccountStatement token={token} clients={clients} />;
      case 'rates-services':
        return <RatesAndServices token={token} />;
      case 'clients':
        return <AdminClients token={token} clients={clients} onRefresh={fetchClients} onNavigate={navigateToSection} navigationParams={navigationParams} />;
      case 'import-clients':
        return <ClientImport />;
      case 'list-lockers':
        return <ListLockersTable token={token} clients={clients} />;
      case 'locker-alerts':
        return <LockerAlerts token={token} clients={clients} />;
      case 'locker-repacks':
        return <LockerRepacks token={token} clients={clients} />;
      case 'lockers':
        return <LockerManagement token={token} clients={clients} />;
      case 'create-manual-wr':
        return <CreateManualWR />;
      case 'create-manual-invoice':
        return <CreateManualInvoice 
          navigationParams={navigationParams} 
          onClearParams={() => setNavigationParams(null)}
          token={token}
        />;
      case 'create-locker':
        return <CreateLocker token={token} />;
      case 'analytics':
        return <AdminAnalytics token={token} />;
      case 'email-logs':
        return <AdminEmailLogs token={token} />;
      case 'wr-detail':
        return <WarehouseDetail token={token} wrId={navigationParams?.wrId} wrData={navigationParams?.wrData} clientData={navigationParams?.clientData} onBack={goBack} onNavigate={navigateToSection} />;
      case 'invoice-detail':
        return <InvoiceDetail token={token} invoiceId={navigationParams?.invoiceId} invoiceData={navigationParams?.invoiceData} onBack={goBack} onNavigate={navigateToSection} />;
      default:
        return <MasterDashboardHome onNavigate={setCurrentSection} />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar 
        userRole="master" 
        onSectionChange={setCurrentSection}
        currentSection={currentSection}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <div className="flex-1 overflow-auto w-full">
        <header className="bg-white shadow-sm border-b sticky top-0 z-30">
          <div className="px-4 md:px-6 py-3 md:py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                <Menu size={24} />
              </button>
              {currentSection !== 'home' && (
                <button 
                  onClick={goBack}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-gray-900"
                  data-testid="back-button"
                  title="Volver atrás"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <div>
                <h1 className="text-lg md:text-2xl font-bold text-gray-900">Panel Master</h1>
                <p className="text-xs md:text-sm text-gray-600 hidden sm:block">{user?.email}</p>
              </div>
            </div>
            <Button onClick={logout} variant="outline" size="sm" className="text-xs md:text-sm">
              <span className="hidden sm:inline">Cerrar </span>Sesion
            </Button>
          </div>
        </header>

        <main className="p-4 md:p-6">
          {renderSection()}
        </main>
      </div>
    </div>
  );
};

export default MasterDashboard;
